import { useUpdateEffect } from '../hooks/useUpdateEffect.js';
import { inView } from '../../libraries/inView.js';
import { subscribe, unsubscribe } from '../../utilities/events.js';
import React, { Children, cloneElement, useState, useEffect, useRef } from 'react-x';
import propTypes from 'prop-types-x';

const cssAnimationDef = ({
  name='fade-in', 
  duration, 
  delay,
  easing,
  target='element', 
  direction='normal', 
  trigger='on-load'
  } = {}) => ({
    'data-animation-target': target,
    'data-animation-name': name,
    ...( delay && { 'data-animation-delay': delay }),
    'data-animation-direction': direction,
    'data-animation-trigger': trigger,
    'style': { 
      ...( duration && { 'animationDuration': duration + 's' }), 
      ...( delay && { 'animationDelay': (typeof delay === 'number' ? delay : parseFloat(delay.split(',')[0]).toFixed(2)) + 's' }),
      ...( easing && { 'animationTimingFunction': easing })
    }
  });

// This is a wrapper utility compnent used to control the addition of html data attributes
// used for running the CSS animations defined in animation styles.
export function CssAnimation(props) {

  const [isInView, setIsInView] = useState(false);
  const [removeElement, setRemoveElement] = useState(false);
  const [redraw, setRedraw] = useState(false);
  const isActiveRef = useRef(false);
  const removingElementInProgressRef = useRef(false);
  const rootElRef = useRef();
  const inViewInstanceRef = useRef();
  const disable = !!props.disable;
  const disableEnter = !!props.disableEnter;
  const disableExit = !!props.disableExit;
  const triggerEnterWhenInViewInit = props.triggerEnter === 'in-view-init';
  const triggerEnterWhenInView = props.triggerEnter === 'in-view';
  const triggerExitWhenOutOfView = props.triggerExit === 'out-of-view';
  const inViewProps = () => Object.assign({}, { root: rootElRef.current }, props.inViewProps || { threshold: 0.1 });
  let triggerEnter = triggerEnterWhenInViewInit || triggerEnterWhenInView ? isInView : !!props.triggerEnter;
  let triggerExit = triggerExitWhenOutOfView ? !isInView : !!props.triggerExit;
  const noEnter = props.enter === undefined;
  const noExit = props.exit === undefined;
  const renderView = !!props.renderView;
  let animationEnterProps = props.enter;
  const animationExitProps = props.exit;
  const onEnterEnd = props.onEnterEnd;
  const onExitEnd = props.onExitEnd;
  const retriggerDependency = Array.isArray(props.retrigger) ? props.retrigger[0] : props.retrigger;
  const retriggerDependencyRef = useRef(retriggerDependency);
  const retriggerProps = Array.isArray(props.retrigger) ? props.retrigger[1] : undefined;
  const retriggerInProgressRef = useRef(false);
  let retrigger = false;

  if (retriggerDependencyRef.current !== retriggerDependency) {
    retrigger = isActiveRef.current && !triggerExit ? true : false;
    retriggerDependencyRef.current = retriggerDependency;
  }

  if (retriggerInProgressRef.current && retriggerProps && isActiveRef.current) {
    animationEnterProps = Object.assign({}, animationEnterProps, retriggerProps);
  }

  function animationEnd(event, removeOnExit) {
    event.stopPropagation(); // prevent bubbling
    if (event.target === event.currentTarget) { // prevent capturing
      if (typeof onEnterEnd === 'function' && triggerEnter) {
        onEnterEnd();
      }
      if (typeof onExitEnd === 'function' && triggerExit) {
        onExitEnd();
      }
      if (removeOnExit) {
        setRemoveElement(prevValue => !prevValue);
      }
    }
  };

  // Get the attributes definition object depending on what the condition is met. 
  // Pass an empty object to disable ot remove animation.
  const animationHTMLAttributes = () => 
    disable || 
    ((disableEnter || noEnter) && !isActiveRef.current) || 
    ((disableExit || noExit) && isActiveRef.current && !retrigger && !triggerEnter )  ? 
      {} :
    retrigger ? 
      cssAnimationDef({ ...animationEnterProps, name: 'none', trigger: 'with-class' }) : 
    triggerEnter ? 
      Object.assign({}, cssAnimationDef({ ...animationEnterProps, trigger: 'with-class' }), { 'className' : 'animation-trigger' }) :
    triggerExit && !noExit && isActiveRef.current ? 
      Object.assign({}, cssAnimationDef({ ...animationExitProps, trigger: 'with-class' }), { 'className' : 'animation-trigger' }) :
    !triggerEnter && !isActiveRef.current ? 
      cssAnimationDef({ ...animationEnterProps, trigger: 'with-class' }) :
    {};

  // This takes the component children and adds the relevant attributes, event handlers
  // and class names to the first child. In this case it will be the animation data attributes,
  // class name to trigger the animation (when required) and the onAnimationEnd so we can hook 
  // into the end of the animation and run a passed in callback if needed.
  function renderChildren(animationProps) {
    return Children.map(props.children, child => {
      return cloneElement(child, {
        ...( child.ref ? { ref: el => { child.ref.current = el; return rootElRef } } : { ref: rootElRef } ),
        ...animationProps,
        style: Object.assign({}, animationProps.style, child.props.style),
        className: `${(child.props.className || '').replace('animation-trigger', '').trim()} ${(animationProps.className || '').trim()}`,
        onAnimationEnd: e => animationEnd(e, props.removeViewOnExit && triggerExit),
      });
    });
  };

  function handleInView(e) {
    const { status, statusDescription, element } = e.detail;
    // Check that the observed element is equal to the current root element
    if (element === rootElRef.current) { 

      if (statusDescription === 'in-view-init' && status) {
        setIsInView(true);
      } 
      else if (statusDescription === 'in-view' && status) {
        setIsInView(true);
      } 
      else if (statusDescription === 'out-of-view' && !status) {
        setIsInView(false);
      } 
    }
  };

  // Create new instance of inView to observe root elemement.
  useEffect(() => {
    if (triggerEnterWhenInViewInit || triggerEnterWhenInView || triggerExitWhenOutOfView) {
      inViewInstanceRef.current = inView(inViewProps());
      inViewInstanceRef.current.init();

      return () => {
        inViewInstanceRef.current.destroy();
      }
    }
  }, []);

  // Subscribe to events dispatched from inView library. 
  useEffect(() => {
    if (triggerEnterWhenInViewInit || triggerEnterWhenInView || triggerExitWhenOutOfView) {
      subscribe('inView', handleInView, window);

      return () => {
        unsubscribe('inView', handleInView, window);
      }
    }
  }, []);

  // If the the element should only animate in once when coming into viewport use the
  // 'in-view-init' prop. Once the element is in view disconnect the observer and remove
  // event listeners from element. 
  useEffect(() => {
    if (triggerEnterWhenInViewInit && isInView) {
      unsubscribe('inView', handleInView, window);
      inViewInstanceRef.current.destroy();
    }
  }, [isInView]);
  
  useUpdateEffect(() => {
    let timer;
    if (isActiveRef.current && !triggerExit) {
      retriggerInProgressRef.current = true;
      timer = setTimeout(() => {
        setRedraw(prevValue => !prevValue);
      }, 1000/60)
    }

    return () => {
      clearTimeout(timer);
    }
  }, [retriggerDependency]);

  useUpdateEffect(() => {
    retriggerInProgressRef.current = false;
  }, [redraw]);

  useUpdateEffect(() => {
    removingElementInProgressRef.current = false;
  }, [removeElement]);

  // Determine whether the view is visible. Even if the enter animation has been disabled 
  // we need to know that it is visble in order to trigger an exit animation (if it applies).
  // isActiveRef - tracks the active visbilitty of the view elements. 
  useEffect(() => {
    if (renderView && (disable || disableEnter || noEnter)) {
      isActiveRef.current = true;
    }
    else if (!renderView) {
      isActiveRef.current = false;
    } 
    else {
      isActiveRef.current = triggerEnter;
    }
  }, [triggerEnter, triggerExit, renderView]);

  return (
    ((renderView && !removingElementInProgressRef.current) || 
    (!renderView && !triggerEnter && triggerExit && isActiveRef.current && !disableExit)) ? 
      renderChildren(animationHTMLAttributes()) : 
      null
  )
};

CssAnimation.propTypes = {
  enter:propTypes.object,
  exit: propTypes.object,
  triggerEnter: propTypes.oneOfType([
    propTypes.string,
    propTypes.bool
  ]),
  triggerExit: propTypes.oneOfType([
    propTypes.string,
    propTypes.bool
  ]),
  retrigger: propTypes.any,
  disable: propTypes.bool,
  disableEnter: propTypes.bool,
  disableExit: propTypes.bool,
  renderView: propTypes.bool,
  onEnterEnd: propTypes.func,
  onExitEnd: propTypes.func,
  removeOnExit: propTypes.bool,
  inViewProps: propTypes.object
};

CssAnimation.defaultProps = {
  renderView: true
};